import React, { useState, lazy, Suspense, useRef, useEffect } from "react";
import { Flex, Text, Card, Button, Badge, IconButton, DropdownMenu, Separator, TextField, Select, Callout, AlertDialog, 
  Tooltip, Dialog, Box, Checkbox } from "@radix-ui/themes";
import { DotsHorizontalIcon, ChevronDownIcon, ChevronUpIcon, LockClosedIcon, InfoCircledIcon, GlobeIcon } from "@radix-ui/react-icons";
import { useLocationContext } from "context";
import { useFetchLocationType, useEditOrgLocation, useGetAllBrands, useCreateBrand } from "hooks";
import { useToast } from "../../context/ToastContext";
import { ToastAlert } from "components";
import { useTimezoneSelect, allTimezones } from "react-timezone-select";

const CreateLocationModal = lazy(() => import("./CreateLocationModal"));

const TruncatedText = ({ children, size, weight, color, style }) => {
  const text = typeof children === 'string' ? children : String(children);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const checkTruncation = () => {
      const container = containerRef.current;
      if (container) {
        setIsTruncated(container.scrollWidth > container.clientWidth);
      }
    };

    // Initial check
    checkTruncation();

    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);

    // Recheck if text changes
    const observer = new MutationObserver(checkTruncation);
    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, characterData: true, subtree: true });
    }

    return () => {
      window.removeEventListener('resize', checkTruncation);
      observer.disconnect();
    };
  }, [text]); // Rerun effect if text content changes

  const textElement = (
    <Box as="span"
      ref={containerRef}
      style={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...style // Allow passing additional styles
      }}
    >
      <Text
        size={size}
        weight={weight}
        color={color}
        style={{width: "100%"}}
      >
        {text}
      </Text>
    </Box>
  );

  return isTruncated ? (
    <Tooltip content={text}>
      {textElement}
    </Tooltip>
  ) : textElement;
};

const OrganisationLocations = () => {
  const { setToastMessage } = useToast();
  // Fetching all the locations info for the organisation
  const { locationsData, refetchLocations } = useLocationContext();
  // Fetching all the location types
  const { data: locationTypes } = useFetchLocationType();
  const [isLoadingBrand, setIsLoadingBrand] = useState(false);

  // Sorting locations by name
  const sortedLocations = Array.isArray(locationsData) ? [...locationsData].sort((a, b) => (a?.name || "").toLowerCase().localeCompare((b?.name || "").toLowerCase())) : [];

  // Hook to update location details
  const updateLocation = useEditOrgLocation();
  const [errors, setErrors] = useState({});

  const [createBrandModalOpen, setCreateBrandModalOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [isCreatingBrand, setIsCreatingBrand] = useState(false);
  const [activeLocationId, setActiveLocationId] = useState(null);
  const createBrandMutation = useCreateBrand();

  const handleCreateBrand = async () => {
    if (!newBrandName.trim()) return;

    // Check if brand name already exists
    const brandExists = brandsData?.some((brand) => brand.name.toLowerCase() === newBrandName.toLowerCase().trim());

    if (brandExists) {
      setToastMessage("A brand with this name already exists");
      return;
    }

    try {
      setIsCreatingBrand(true);
      const response = await createBrandMutation.mutateAsync({
        orgID: sortedLocations?.[0]?.organisation,
        data: {
          name: newBrandName,
          location_ids: [],
        },
      });

      setToastMessage(`Brand "${newBrandName}" created successfully`);
      
      // Get the ID directly from the response instead of refetching
      if (activeLocationId && response.data.id) {
        handleFieldChange(activeLocationId, "brand_id", response.data.id);
      }

      // Reset state
      setNewBrandName("");

      // Close modal after a short delay to ensure state updates
      setTimeout(() => {
        setCreateBrandModalOpen(false);
        setActiveLocationId(null);
      }, 100);
    } catch (error) {
      console.error("Brand creation error:", error);
      setToastMessage(error.response?.data?.message || "Failed to create brand");
    } finally {
      setIsCreatingBrand(false);
    }
  };

  // Function to handle brand dropdown selection
  const handleBrandSelection = (locationId, value) => {
  if (value === "create_brand") {
    setActiveLocationId(locationId);
    setCreateBrandModalOpen(true);
  } else if (value === "_placeholder") {
    // If "None" is selected, remove brand_id from modifiedLocations
    // to prevent it from being treated as a change
    setModifiedLocations((prevLocations) => {
      const updated = { ...prevLocations };
      
      // If there's an entry for this location but only contains brand_id
      if (updated[locationId] && Object.keys(updated[locationId]).length === 1 && 'brand_id' in updated[locationId]) {
        delete updated[locationId]; // Remove the entire location entry
      } else if (updated[locationId]) {
        // If location has other modified fields, just remove brand_id
        const { brand_id, ...rest } = updated[locationId];
        updated[locationId] = rest;
      }
      
      return updated;
    });
  } else {
    // For actual brand selections
    handleFieldChange(locationId, "brand_id", parseInt(value));
  }
};

  // For the dropdown menu
  const [dropdownStates, setDropdownStates] = useState({});
  const handleShowDropdown = (locationId) => () => {
    setDropdownStates((prevStates) => ({
      ...prevStates,
      [locationId]: !prevStates[locationId],
    }));
  };

  const [modifiedLocations, setModifiedLocations] = useState({});

  const handleFieldChange = (locationId, field, value) => {
    if (errors[locationId]?.[field]) {
      setErrors({
        ...errors,
        [locationId]: {
          ...errors[locationId],
          [field]: undefined,
        },
      });
    }
    setModifiedLocations((prevLocations) => ({
      ...prevLocations,
      [locationId]: {
        ...prevLocations[locationId],
        [field]: value,
      },
    }));
  };

  // Fetch all brands for the organization
  const { data: brandsData } = useGetAllBrands(sortedLocations?.[0]?.organisation);

  // Filter out default brand
  const nonDefaultBrands = brandsData?.filter((brand) => !brand.is_default) || [];

  const handleSaveChanges = (locationId) => {
    const currentName = modifiedLocations[locationId]?.name ?? sortedLocations.find((loc) => loc.id === locationId).name;
    const currentAddress = modifiedLocations[locationId]?.address ?? sortedLocations.find((loc) => loc.id === locationId).address;

    // Validate fields
    const locationErrors = {};
    if (!currentName || currentName.trim() === "") {
      locationErrors.name = "Input location name.";
    }

    if (!currentAddress || currentAddress.trim() === "") {
      locationErrors.address = "Input location address.";
    }

    if (Object.keys(locationErrors).length > 0) {
      setErrors({ ...errors, [locationId]: locationErrors });
      return;
    }

    updateLocation.mutate(
      {
        id: locationId,
        name: modifiedLocations[locationId]?.name,
        address: modifiedLocations[locationId]?.address,
        location_type: locationTypes.data.find((type) => type.name === modifiedLocations[locationId]?.location_type)?.id,
        timezone: modifiedLocations[locationId]?.timezone,
      },
      {
        onSuccess: () => {
          setToastMessage("Changes saved");
          setModifiedLocations({});
          setErrors({});
          refetchLocations();
        },
      }
    );
  };

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmingLocationId, setConfirmingLocationId] = useState(null);

  // Handle Save Brand button click - now opens the dialog instead of directly saving
  const handleBrandSaveClick = (locationId) => {
    setConfirmingLocationId(locationId);
    setIsConfirmDialogOpen(true);
  };

  // Actual function to save brand changes after confirmation
  const handleSaveBrandChanges = (locationId) => {
    setIsLoadingBrand(true);
    let brandIdToSend = modifiedLocations[locationId]?.brand_id;

    // If null/unassigned is selected, find the default brand
    if (brandIdToSend === null) {
      const defaultBrand = brandsData?.find((brand) => brand.is_default);
      if (defaultBrand) {
        brandIdToSend = defaultBrand.id;
      }
    }

    // Create a payload specifically for brand updates
    const payload = {
      id: locationId,
      brand_id: brandIdToSend,
    };

    // Make the PATCH request with just the brand_id
    updateLocation.mutate(payload, {
      onSuccess: () => {
        setToastMessage("Changes saved");
        setModifiedLocations((prev) => {
          const updated = { ...prev };
          delete updated[locationId];
          return updated;
        });
        refetchLocations();
      },
    });
    setIsLoadingBrand(false);
  };

  const locationsWithDefaultBrand = sortedLocations.filter((location) => {
    const currentBrand = brandsData?.find((b) => b.id === location.brand_id);
    return !currentBrand || currentBrand.is_default;
  }).length;

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);

  const labelStyle = "original";
  const timezones = {...allTimezones, "Europe/Berlin": "Frankfurt",};
  const { options: timezoneOptions, parseTimezone } = useTimezoneSelect({ labelStyle, timezones });

  // Getting user's timezone as fallback
  const getUserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.warn('Could not detect user timezone, falling back to UTC');
      return 'UTC';
    }
  };

  return (
    <React.Fragment>
      <Flex direction="column" gap="5" height="100%" style={{ alignSelf: "stretch", flex: "1 0 0" }}>
        {/* Header with stats and action buttons */}
        <Flex justify="between" align="start" width="100%">
          {/* Left side: Location count */}
          <Text size="1" weight="light" color="gray" style={{ paddingTop: "var(--space-3)" }}>
            {sortedLocations.length} {sortedLocations.length === 1 ? "location" : "locations"} total
          </Text>

          {/* Right side: Callout about unassigned brands */}
          {locationsWithDefaultBrand > 0 && (
            <Callout.Root size="1" color="yellow">
              <Callout.Icon>
                <InfoCircledIcon />
              </Callout.Icon>
              <Callout.Text>
                {locationsWithDefaultBrand} {locationsWithDefaultBrand === 1 ? "location has" : "locations have"} not been assigned to a brand.
              </Callout.Text>
            </Callout.Root>
          )}
        </Flex>

        {/* Location list container - separate from header */}
        <Flex
          className="location-details-container"
          direction="column"
          gap="5"
          style={{ alignItems: "flex-start", flex: "1 0 0", alignSelf: "stretch", overflow: "scroll", maxHeight: "calc(100vh - 184px)" }}
        >
          {sortedLocations.length === 0 && (
            <Flex
              direction="column"
              style={{ justifyContent: "center", alignItems: "center", alignSelf: "stretch", border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)", height: "100%" }}
            >
              <Flex direction="column" gap="5" maxWidth="296px" style={{ alignItems: "center", width: "100%" }}>
                <Flex gap="2" direction="column" style={{ alignItems: "center", alignSelf: "stretch", justifyContent: "center" }}>
                  <Text size="4" weight="medium" align="center">
                    No locations
                  </Text>
                  <Text size="2" weight="regular" align="center" color="gray">
                    Create your first location to start activating your integrations.
                  </Text>
                </Flex>
                <Suspense>
                  <CreateLocationModal buttontype="soft" />
                </Suspense>
              </Flex>
            </Flex>
          )}
          {sortedLocations &&
            sortedLocations.map((location) => (
              <Card key={location.id}
                style={{ display: "flex", flexDirection: "column", flex: "0 0 auto", alignItems: "flex-start", 
                  alignSelf: "stretch", gap: "var(--space-4)", padding: "var(--space-4)" }}>
                {/* Locations Header Section */}
                <Flex gap="2"
                  style={{ alignSelf: "stretch", alignItems: "center", justifyContent: "space-between" }}>
                  {/* Location Name & Address */}
                  <Flex direction="column" style={{ alignSelf: "stretch", alignItems: "flex-start", width: "30%" }}>
                    <TruncatedText size="2" weight="medium">
                      {location.name}
                    </TruncatedText>
                    <TruncatedText size="1" weight="medium" color="gray" style={{ width: "100%" }}>
                      {location.address}
                    </TruncatedText>
                  </Flex>

                  {/* Information Badges */}
                  <Flex gap="2" style={{ alignSelf: "stretch", alignItems: "center", 
                    justifyContent: "space-between", width: "70%" }}>
                    {/* No of access points */}
                    <Flex style={{ width: "100%", justifyContent: "center" }}>
                      <Badge size="1" variant="soft" color="gray">
                        <TruncatedText size="1">
                          {`${location.access_point_mac_list.length} ${location.access_point_mac_list.length === 1 
                            ? "Wi-Fi access point" 
                            : "Wi-Fi access points"}`
                          }
                        </TruncatedText>
                      </Badge>
                    </Flex>
                    {/* No of integrations */}
                    <Flex style={{ width: "100%", justifyContent: "center" }}>
                      <Badge size="1" variant="soft" color="gray">
                        <TruncatedText size="1">
                          {`${location.integrations.length} ${location.integrations.length === 1 ? "integration" : "integrations"}`}
                        </TruncatedText>
                      </Badge>
                    </Flex>
                    {/* Location Type */}
                    <Flex style={{ width: "100%", justifyContent: "center" }}>
                      <TruncatedText size="1" color="gray" style={{textAlign: "center" }}>
                        {location.location_type_name}
                      </TruncatedText>
                    </Flex>
                    {/* Associate brand */}
                    <Flex style={{ width: "100%", justifyContent: "center" }}>
                      <TruncatedText size="1" color="gray" style={{textAlign: "center" }}>
                        {(() => {
                          const brand = brandsData?.find((b) => b.id === location.brand_id);
                          return brand?.is_default ? 'No brand' : brand?.name;
                        })()}
                      </TruncatedText>
                    </Flex>
                    {/* Possible Actions */}
                    <Flex p="1" gap="4" style={{ alignItems: "center" }}>
                      {/* Ellipsis Icon for Deleting Location */}
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <IconButton size="1" variant="ghost" color="gray">
                            <DotsHorizontalIcon width="16" height="16" />
                          </IconButton>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content>
                          <DropdownMenu.Item
                            color="red"
                            onClick={() => {
                              const supportEmail = "support@b-connect.co.uk";
                              const subject = "Delete Account Request";
                              const body = `Hello, I need help with deleting this location ${location.name}(id: ${location.id}). Please assist me with this process.`;
                              const mailtoLink = `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                              window.location.href = mailtoLink;
                            }}
                          >
                            Delete
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Root>

                      {/* Arrow Icon for Editing Location Details */}
                      <IconButton size="1" variant="ghost" color="gray" onClick={handleShowDropdown(location.id)}>
                        {dropdownStates[location.id] ? <ChevronUpIcon width="16" height="16" /> : <ChevronDownIcon width="16" height="16" />}
                      </IconButton>
                    </Flex>
                  </Flex>
                </Flex>

                {/* Location Details Section */}
                {dropdownStates[location.id] && (
                  <Flex direction="column" gap="4" style={{ alignSelf: "stretch", alignItems: "flex-start" }}>
                    <Separator size="4" orientation="horizontal" />

                    {/* Location Details */}
                    <Flex gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                      <Flex direction="column" gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "40%" }}>
                        <Text size="2" weight="medium">
                          Details
                        </Text>
                        <Text size="1" weight="regular" color="gray">
                          Share your location name and address to enhance your integrations.
                        </Text>
                      </Flex>
                      {/* Name and Type Fields */}
                      <Flex direction="column" gap="4" style={{ alignItems: "flex-end", alignSelf: "stretch", width: "60%", flex: "1 0 0" }}>
                        <Flex direction="row" gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch", justifyContent: "center" }}>
                          {/* Name Field */}
                          <Flex direction="column" gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "100%" }}>
                            <Text size="2" weight="medium" style={{ width: "100%" }}>
                              Name
                            </Text>
                            <TextField.Root
                              size="2"
                              style={{ width: "100%" }}
                              value={modifiedLocations[location.id]?.name ?? location.name}
                              onChange={(e) => handleFieldChange(location.id, "name", e.target.value)}
                            />
                            {errors[location.id]?.name && (
                              <Text size="1" color="red">
                                {errors[location.id].name}
                              </Text>
                            )}
                          </Flex>

                          {/* Type Select Field */}
                          <Flex direction="column" gap="1" style={{ alignItems: "center", alignSelf: "stretch", width: "100%" }}>
                            <Text size="2" weight="medium" style={{ width: "100%" }}>
                              Type
                            </Text>
                            <Select.Root style={{ width: "100%" }} 
                              defaultValue={location.location_type_name} 
                              onValueChange={(value) => handleFieldChange(location.id, "location_type", value)}>
                              <Select.Trigger variant="surface" style={{ width: "100%" }} />
                              <Select.Content position="popper">
                                <Select.Group>
                                  {locationTypes.data.map((type, index) => (
                                    <Select.Item key={index} value={type.name}>
                                      {type.name}
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              </Select.Content>
                            </Select.Root>
                          </Flex>
                        </Flex>

                        {/* Address Field */}
                        <Flex direction="column" gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Text size="2" weight="medium" style={{ width: "100%" }}>
                            Address
                          </Text>
                          <TextField.Root
                            size="2"
                            style={{ width: "100%" }}
                            value={modifiedLocations[location.id]?.address ?? location.address}
                            onChange={(e) => handleFieldChange(location.id, "address", e.target.value)}
                          />
                          {errors[location.id]?.address && (
                            <Text size="1" color="red">
                              {errors[location.id].address}
                            </Text>
                          )}
                        </Flex>

                        {/* Save Changes Button */}
                        <Button
                          size="2"
                          variant="solid"
                          style={{ cursor: modifiedLocations[location.id] ? "default" : "not-allowed" }}
                          disabled={
                            !modifiedLocations[location.id] ||
                            ((modifiedLocations[location.id]?.name ?? location.name) === location.name &&
                              (modifiedLocations[location.id]?.location_type ?? location.location_type) === location.location_type &&
                              (modifiedLocations[location.id]?.address ?? location.address) === location.address)
                          }
                          onClick={() => handleSaveChanges(location.id)}
                        >
                          Save changes
                        </Button>
                      </Flex>
                    </Flex>
                    <Separator size="4" orientation="horizontal" />

                    {/* Brand section */}
                    <Flex gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                      <Flex gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "40%" }}>
                        <Flex p="1" gap="2" align="center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" 
                            strokeWidth={1.5} stroke="currentColor" className="size-3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z" />
                          </svg>
                        </Flex>
                        <Text size="2" weight="medium">Brand</Text>
                      </Flex>
                      <Flex direction="column" gap="4" style={{ alignItems: "flex-end", alignSelf: "stretch", width: "60%", flex: "1 0 0" }}>
                        {/* Brand Select Field */}
                        <Flex direction="column" gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "100%" }}>
                          {(() => {
                            const isBrandLocked = location.brand_id && !brandsData?.find((b) => b.id === location.brand_id)?.is_default;
                            const currentBrand = brandsData?.find((b) => b.id === location.brand_id);
                            const isDefaultBrand = currentBrand?.is_default;

                            if (isBrandLocked && currentBrand) {
                              return (
                                <Tooltip content="This location's brand is locked. To use a different brand, create a new location or contact support.">
                                  <Flex style={{ width: "100%", position: "relative" }}>
                                    <TextField.Root style={{ width: "100%" }} value={currentBrand.name} readOnly disabled />
                                    <LockClosedIcon
                                      width="16"
                                      height="16"
                                      style={{
                                        position: "absolute",
                                        right: "12px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                      }}
                                    />
                                  </Flex>
                                </Tooltip>
                              );
                            }

                            return (
                              <Select.Root
                                style={{ width: "100%" }}
                                value={
                                  modifiedLocations[location.id]?.brand_id !== undefined
                                    ? modifiedLocations[location.id].brand_id === null
                                      ? "" // Use empty string to show placeholder
                                      : String(modifiedLocations[location.id].brand_id)
                                    : location.brand_id && !isDefaultBrand
                                    ? String(location.brand_id)
                                    : "" // Use empty string to show placeholder
                                }
                                onValueChange={(value) => {
                                  handleBrandSelection(location.id, value);
                                }}
                              >
                                <Select.Trigger
                                  variant="surface"
                                  style={{
                                    width: "100%",
                                  }}
                                  placeholder="Select..."
                                />
                                <Select.Content position="popper">
                                  <Select.Group>
                                    <Select.Item value="_placeholder">
                                      None
                                    </Select.Item>
                                    {nonDefaultBrands.map((brand) => (
                                      <Select.Item key={brand.id} value={String(brand.id)}>
                                        {brand.name}
                                      </Select.Item>
                                    ))}
                                    <Select.Item value="create_brand">
                                      <Flex gap="1" align="center">
                                        <Text>Create brand...</Text>
                                      </Flex>
                                    </Select.Item>
                                  </Select.Group>
                                </Select.Content>
                              </Select.Root>
                            );
                          })()}
                        </Flex>

                        <AlertDialog.Root>
                          <AlertDialog.Trigger asChild>
                            <Button
                              size="2"
                              variant="solid"
                              style={{ cursor: modifiedLocations[location.id]?.brand_id !== undefined ? "default" : "not-allowed" }}
                              disabled={modifiedLocations[location.id]?.brand_id === undefined || (location.brand_id && !brandsData?.find((b) => b.id === location.brand_id)?.is_default)}
                              loading={isLoadingBrand}>
                              Save changes
                            </Button>
                          </AlertDialog.Trigger>
                          <AlertDialog.Content>
                            <AlertDialog.Title>Confirm Brand Selection</AlertDialog.Title>
                            <AlertDialog.Description>
                              {/* Slot container */}
                              <Flex gap="4" direction="column">
                                {/* Lock content */}
                                <Flex gap="2" style={{ alignItems: 'flex-start', alignSelf: 'stretch'}}>
                                  <Flex p="1" gap="2" align="center">
                                    <LockClosedIcon width={12} height={12} />
                                  </Flex>
                                  <Text size="2">
                                    This location will be locked to the selected brand. You won't be able to change it later.
                                  </Text>
                                </Flex>
                                {/* Swatch content */}
                                <Flex gap="2" style={{ alignItems: 'flex-start', alignSelf: 'stretch'}}>
                                  <Flex p="1" gap="2" align="center">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" 
                                      strokeWidth={1.5} stroke="currentColor" className="size-3">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z" />
                                    </svg>
                                  </Flex>
                                  <Text size="2">
                                    This location will adopt the selected brand's captive portal design, network names, 
                                    passwords, and QR codes.
                                  </Text>
                                </Flex>
                                {/* Acceptance checkbox */}
                                <Flex p="4"
                                  style={{ border: '1px solid var(--gray-a3', borderRadius: 'var(--radius-2)',}}>
                                  <Text as="label" size="2">
                                    <Flex gap="2">
                                      <Checkbox size="2" variant="surface" 
                                        checked={isCheckboxChecked}
                                        onCheckedChange={(checked) => setIsCheckboxChecked(checked)} />
                                        I understand this location will be permanently locked to this brand and adopt its 
                                        captive portal, network names, passwords, and QR codes.
                                    </Flex>
                                  </Text>
                                </Flex>
                              </Flex>
                            </AlertDialog.Description>

                            <Flex gap="3" justify="end" mt="4">
                              <AlertDialog.Cancel>
                                <Button variant="soft" color="gray">
                                  Cancel
                                </Button>
                              </AlertDialog.Cancel>
                              <AlertDialog.Action>
                                <Button variant="solid"
                                  onClick={() => handleSaveBrandChanges(location.id)}
                                  disabled={!isCheckboxChecked}>
                                  Save changes
                                </Button>
                              </AlertDialog.Action>
                            </Flex>
                          </AlertDialog.Content>
                        </AlertDialog.Root>
                      </Flex>
                    </Flex>
                    <Separator size="4" orientation="horizontal" />

                    {/* Timezone Selector */}
                    <Flex gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                      <Flex gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "40%" }}>
                        <Flex p="1" gap="2" align="center">
                          <GlobeIcon width={12} height={12} />
                        </Flex>
                        <Text size="2" weight="medium">Location Timezone</Text>
                      </Flex>
                      <Flex direction="column" gap="4" style={{ alignItems: "flex-end", alignSelf: "stretch", width: "60%", flex: "1 0 0" }}>
                        {/* Timezone Select Field */}
                        <Flex direction="column" gap="1" style={{ alignItems: "center", alignSelf: "stretch", width: "100%" }}>
                          <Select.Root style={{ width: "100%" }}
                            value={modifiedLocations[location.id]?.timezone ?? location.timezone ?? getUserTimezone()}
                            onValueChange={(value) => handleFieldChange(location.id, "timezone", value)}>
                            <Select.Trigger variant="surface" style={{ width: "100%" }} />
                            <Select.Content position="popper">
                              <Select.Group>
                                {timezoneOptions?.map((timezone) => (
                                  <Select.Item key={timezone.value} value={timezone.value}>
                                    {timezone.label}
                                  </Select.Item>
                                ))}
                              </Select.Group>
                            </Select.Content>
                          </Select.Root>
                        </Flex>
                        <Button
                          size="2"
                          variant="solid"
                          style={{ cursor: modifiedLocations[location.id]?.timezone && 
                            (modifiedLocations[location.id]?.timezone !== (location.timezone ?? getUserTimezone())) 
                            ? "default" : "not-allowed" }}
                          disabled={!modifiedLocations[location.id]?.timezone || 
                            (modifiedLocations[location.id]?.timezone === (location.timezone ?? getUserTimezone()))}
                          onClick={() => handleSaveChanges(location.id)}>
                          Save changes
                        </Button>
                      </Flex>
                    </Flex>
                  </Flex>
                )}
              </Card>
            ))}
        </Flex>
      </Flex>

      <Dialog.Root open={createBrandModalOpen} onOpenChange={setCreateBrandModalOpen}>
        <Dialog.Content maxWidth="400px">
          <Dialog.Title>Create Brand</Dialog.Title>
          <Flex direction="column" gap="3">
            <TextField.Root value={newBrandName} onChange={(e) => setNewBrandName(e.target.value)} placeholder="New brand name" style={{ width: "100%" }} />
          </Flex>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleCreateBrand} loading={isCreatingBrand} disabled={!newBrandName.trim()}>
              Create
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <ToastAlert />
    </React.Fragment>
  );
};

export default OrganisationLocations;
